import { Link } from 'react-router-dom';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './APropos.css';

const MISSION_ICONS = [
  {
    svg: <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></>,
    title: 'Excellence pédagogique',
    desc: 'Curriculum de niveau international, actualisé en continu.',
  },
  {
    svg: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    title: 'Insertion garantie',
    desc: "Accompagnement actif vers l'emploi dès la première semaine.",
  },
  {
    svg: <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    title: 'Ancrage africain',
    desc: "Programmes adaptés aux réalités du marché ouest-africain.",
  },
  {
    svg: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
    title: 'Innovation continue',
    desc: "IA, data, fullstack — toujours au sommet des technologies.",
  },
];

const TEAM = [
  { name: 'Équipe Tech', role: 'Formateurs Full-Stack & IA', desc: "Développeurs seniors avec 10+ ans d'expérience dans des entreprises tech africaines et internationales." },
  { name: 'Équipe Data', role: 'Formateurs Data & Analytics', desc: "Data scientists et analysts issus de banques, télécoms et institutions régionales." },
  { name: 'Équipe Carrières', role: 'Conseillers en insertion', desc: "Experts RH et recruteurs avec un réseau de 100+ entreprises partenaires en Afrique de l'Ouest." },
];

const HERO_STATS = [
  { num: '500+', label: 'Alumni formés depuis 2021', bar: '85%', delay: '0s', barDelay: '0.2s', svg: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
  { num: '94%', label: "Taux d'insertion professionnelle", bar: '94%', delay: '1.5s', barDelay: '0.4s', svg: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></> },
  { num: '4 ans', label: "D'expertise en formation tech", bar: '70%', delay: '3s', barDelay: '0.6s', svg: <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></> },
  { num: '80+', label: 'Entreprises partenaires actives', bar: '75%', delay: '4.5s', barDelay: '0.8s', svg: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></> },
];

export default function APropos() {
  const { sections, page } = useCmsPage('a-propos');
  useSeoMeta(page, { title: 'À propos — Tech Talent Accelerator', desc: "L'équipe et la mission de TTA, accélérateur de talents tech en Afrique de l'Ouest." });
  const getSec = type => sections.find(s => s.type === type)?.content || {};

  const hero      = getSec('hero');
  const heroStats = getSec('hero_stats');
  const mission   = getSec('mission_grid');
  const team      = getSec('team_grid');
  const partners  = getSec('partners_block');
  const cta       = getSec('cta_dark');

  const filterTabs = hero.filter_tabs || [
    { key: 'mission', label: 'Notre mission' },
    { key: 'equipe', label: 'Notre équipe' },
    { key: 'partenariats', label: 'Partenariats' },
    { key: 'engagements', label: 'Engagements' },
  ];
  const phmCards = (heroStats.items || HERO_STATS).map((item, i) => ({
    ...HERO_STATS[i], ...item,
  }));
  const missionIcons = (mission.icons || MISSION_ICONS).map((item, i) => ({
    ...MISSION_ICONS[i], ...item,
  }));
  const teamMembers = team.members || TEAM;
  const countryTags = (partners.country_tags || "Côte d'Ivoire,Sénégal,Afrique de l'Ouest").split(',').map(t => t.trim());

  return (
    <main className="apropos-page">

      {/* ── HERO ── */}
      <section className="apropos-hero">
        <div className="apropos-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.55" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div className="page-hero-layout">
          <div className="page-hero-left">
            <div className="section-eyebrow">{hero.eyebrow || 'À propos de TTA'}</div>
            <h1 className="section-title" style={{ fontSize: '44px', maxWidth: '560px' }}>
              {hero.title || 'Former les talents tech'}<br />
              <span className="grad-text">{hero.title_grad || "qui transforment l'Afrique"}</span>
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '480px' }}>
              {hero.subtitle || "Tech Talent Accelerator est né d'une conviction : le numérique est le levier le plus puissant pour l'insertion professionnelle et la croissance économique en Afrique de l'Ouest."}
            </p>
            <div className="hero-filter-bar">
              {filterTabs.map(f => (
                <a key={f.key} href={`#${f.key}`} className="hero-filter-btn">{f.label}</a>
              ))}
            </div>
          </div>
          <div className="page-hero-right">
            <div className="phm-grid">
              {phmCards.map((s, i) => (
                <div key={i} className="phm-card" style={{ animationDelay: s.delay }}>
                  <div className="phm-card__icon">
                    <svg viewBox="0 0 24 24">{s.svg}</svg>
                  </div>
                  <div className="phm-card__num">{s.num}</div>
                  <div className="phm-card__label">{s.label}</div>
                  <div className="phm-card__bar">
                    <div className="phm-card__bar-fill" style={{ width: s.bar, animationDelay: s.barDelay }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section id="mission" className="section">
        <div className="apropos-mission-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', alignItems: 'center' }}>
          <div>
            <div className="section-eyebrow">{mission.eyebrow || 'Notre mission'}</div>
            <h2 className="section-title" style={{ fontSize: '30px' }}>
              {mission.h2 || 'Accélérer les carrières tech'}<br />
              {mission.h2_br || 'pour un impact durable'}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '16px' }}>
              {mission.p1 || "TTA forme des professionnels du numérique capables de répondre aux besoins actuels des entreprises africaines et internationales. Notre approche combine rigueur pédagogique, projets pratiques et accompagnement vers l'emploi."}
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '24px' }}>
              {mission.p2 || "Nous croyons que chaque talent mérite une chance d'accéder à l'économie numérique, quelle que soit son origine. C'est pourquoi nous proposons des programmes financés par des bailleurs institutionnels mais aussi accessibles sur fonds propres."}
            </p>
            <Link to={mission.cta_href || '/formations'} className="btn-primary">{mission.cta_label || 'Découvrir nos formations →'}</Link>
          </div>
          <div className="apropos-mission-icons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {MISSION_ICONS.map(item => (
              <div key={item.title} style={{ background: 'var(--bg-card)', border: '0.5px solid var(--border-color)', borderRadius: '12px', padding: '20px' }}>
                <div style={{ width: '40px', height: '40px', background: 'var(--grad)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{item.svg}</svg>
                </div>
                <div style={{ fontFamily: 'var(--fs)', fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{item.title}</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ÉQUIPE ── */}
      <section id="equipe" className="section section-alt">
        <div className="section-eyebrow">{team.eyebrow || 'Notre équipe'}</div>
        <h2 className="section-title">{team.h2 || 'Des experts du terrain'}</h2>
        <p className="section-subtitle">{team.subtitle || "Nos formateurs sont des professionnels actifs dans l'industrie tech."}</p>
        <div className="apropos-team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {teamMembers.map(member => (
            <div key={member.name} style={{ background: 'var(--bg-card)', border: '0.5px solid var(--border-color)', borderRadius: '14px', padding: '24px', textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', background: 'var(--grad)', borderRadius: '50%', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </div>
              <div style={{ fontFamily: 'var(--fs)', fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{member.name}</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>{member.role}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.65 }}>{member.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PARTENARIATS ── */}
      <section id="partenariats" className="section">
        <div className="apropos-partners-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', alignItems: 'center' }}>
          <div style={{ background: 'linear-gradient(135deg, #0d1f14, #1a3a22)', borderRadius: '16px', padding: '36px 32px' }}>
            <div style={{ fontFamily: 'var(--fs)', fontSize: '18px', fontWeight: 700, color: '#fff', lineHeight: 1.4, marginBottom: '16px' }}>
              {partners.box_title || "Porté par une structure avec plus de 20 ans d'expérience en transformation numérique en Afrique."}
            </div>
            <div style={{ fontSize: '15px', color: 'rgba(255,255,255,.55)', lineHeight: 1.7 }}>
              {partners.box_desc || "TTA bénéficie de l'expertise et du réseau de Sira-Labs, cabinet de conseil en transformation digitale présent en Côte d'Ivoire, au Sénégal et dans plusieurs pays d'Afrique de l'Ouest."}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
              {countryTags.map(tag => (
                <span key={tag} style={{ fontSize: '10px', fontWeight: 600, padding: '4px 10px', background: 'rgba(139,197,63,.12)', border: '0.5px solid rgba(139,197,63,.2)', borderRadius: '6px', color: '#8BC53F', letterSpacing: '.06em' }}>{tag}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="section-eyebrow">{partners.academic_eyebrow || 'Notre partenariat académique'}</div>
            <h2 className="section-title" style={{ fontSize: '26px' }}>
              {partners.academic_h2 || 'Un curriculum reconnu'}<br />
              {partners.academic_h2_end || "à l'international"}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '16px' }}>
              {partners.academic_p1 || "TTA s'appuie sur un partenariat académique avec Developers Institute, école de code classée dans le top 31 mondial. Ce partenariat garantit un curriculum de niveau international, actualisé en permanence selon les besoins du marché."}
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>
              {partners.academic_p2 || "Nos certifications sont reconnues par les employeurs à l'échelle mondiale, ouvrant des opportunités de carrière bien au-delà de l'Afrique de l'Ouest."}
            </p>
          </div>
        </div>
      </section>

      {/* ── NOTRE ÉCOSYSTÈME — TECH INNOVATION ACCELERATOR ── */}
      <section className="section" id="ecosysteme">
        <div className="tia-block">
          <div className="tia-block__text">
            <div className="section-eyebrow">Notre écosystème</div>
            <h2 style={{ fontFamily: 'var(--fs)', fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.5px', margin: '4px 0 14px' }}>
              Tech Innovation Accelerator
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '14px' }}>
              <strong>Tech Innovation Accelerator (TIA)</strong> est une fabrique de talents, de solutions
              et de startups. Le programme part des défis réels pour former des jeunes de 18 à 25 ans,
              concevoir des services numériques intégrant l'IA, puis tester les solutions les plus viables
              avec leurs futurs utilisateurs.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '22px' }}>
              Découvrez le programme et ses activités sur son site dédié.
            </p>
            <a
              href="https://techinnovationaccelerator.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ fontSize: '14px', padding: '13px 24px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Découvrir Tech Innovation Accelerator
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
            </a>
          </div>
          <div className="tia-block__aside" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6M10 22h4" />
              <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
            </svg>
          </div>
        </div>
      </section>

      {/* ── CTA / ENGAGEMENTS ── */}
      <section id="engagements" className="apropos-cta-section" style={{ padding: '72px 40px', background: 'linear-gradient(135deg, #0d1f14, #1a3a22)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(79,166,70,.14) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <h2 style={{ fontFamily: 'var(--fs)', fontSize: '34px', fontWeight: 800, color: '#fff', letterSpacing: '-0.8px', marginBottom: '14px', position: 'relative', zIndex: 1 }}>
          {cta.h2 || "Rejoignez l'aventure"} <span className="grad-text">{cta.h2_grad || 'TTA'}</span>
        </h2>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,.5)', marginBottom: '28px', position: 'relative', zIndex: 1 }}>
          {cta.p || 'Candidat, bailleur ou entreprise — nous avons un rôle pour vous.'}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Link to={cta.cta1_href || '/candidater'} className="btn-primary" style={{ fontSize: '14px', padding: '13px 26px' }}>{cta.cta1_label || 'Candidater →'}</Link>
          <Link to={cta.cta2_href || '/contact'} style={{ fontFamily: 'var(--fs)', fontSize: '14px', fontWeight: 500, padding: '13px 20px', background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.75)', borderRadius: '9px' }}>
            {cta.cta2_label || "Contacter l'équipe"}
          </Link>
        </div>
      </section>

    </main>
  );
}
